// Upsell opportunities — NEW console page (18H §4.2). The opportunity ledger
// the T1 nudge engine writes to (trust-lifecycle.service.ts): open → nudged →
// converted | dismissed. value_estimate is a PLACEHOLDER (rate card TODO,
// 17_ §9 item 13) — rendered honestly, not hidden.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { RefreshCw, PlayCircle, X } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Kpi } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/ToastProvider';
import {
  getOpportunities, dismissOpportunity, runLifecycle, nudgeTypeLabel, orgNameFor,
  type TrustUpsellOpportunityRow, type TrustUpsellState,
} from '@/lib/api/trust';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };

const STATE_TABS: Array<{ key: TrustUpsellState | ''; label: string }> = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'nudged', label: 'Nudged' },
  { key: 'converted', label: 'Converted' },
  { key: 'dismissed', label: 'Dismissed' },
];

export function OpportunitiesPage() {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const { toast } = useToast();
  const [rows, setRows] = useState<TrustUpsellOpportunityRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<TrustUpsellState | ''>('');
  const [dismissTarget, setDismissTarget] = useState<TrustUpsellOpportunityRow | null>(null);
  const [runningEngine, setRunningEngine] = useState(false);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getOpportunities();
      setRows(data || []);
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (tab ? rows.filter((r) => r.state === tab) : rows), [rows, tab]);

  const kpis = useMemo(() => {
    const open = rows.filter((r) => r.state === 'open').length;
    const nudged = rows.filter((r) => r.state === 'nudged').length;
    const converted = rows.filter((r) => r.state === 'converted').length;
    const conversionRate = rows.length > 0 ? Math.round((converted / rows.length) * 100) : 0;
    return { total: rows.length, open, nudged, converted, conversionRate };
  }, [rows]);

  const doDismiss = async (row: TrustUpsellOpportunityRow) => {
    try {
      await dismissOpportunity(row.id);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, state: 'dismissed', dismissed_at: new Date().toISOString() } : r)));
      toast({ kind: 'success', title: 'Opportunity dismissed.' });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [row.id]: err instanceof Error ? err.message : 'Failed to dismiss' }));
    }
  };

  const runEngine = async () => {
    setRunningEngine(true);
    try {
      const result = await runLifecycle();
      const held = result.heldForBridge ? `, ${result.heldForBridge} held (delivery not built)` : '';
      toast({ kind: 'success', title: `Nudge engine ran: ${result.opportunitiesOpened} opened, ${result.opportunitiesNudged} nudged${held}.` });
      await load();
    } catch (err) {
      toast({ kind: 'error', title: 'Lifecycle run failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setRunningEngine(false);
    }
  };

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[140, 100, 70, 80, 80, 60]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Upsell opportunities</h1>
          <p className="page-subtitle">Trigger-driven ledger the nudge engine opens and advances (17_ §6). value_estimate is a placeholder pending the rate card.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
          <button onClick={runEngine} disabled={runningEngine} style={{ ...btnSec, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' }}>
            <PlayCircle size={13} /> {runningEngine ? 'Running…' : 'Run nudge engine now'}
          </button>
        </div>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="trending-up"
        emptyTitle="No opportunities yet"
        emptyBody="The nudge engine hasn't opened any opportunities. Run it manually above, or wait for the next scheduled pass once trust orgs have telemetry."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Total opportunities" value={kpis.total} icon="trending-up" />
          <Kpi label="Open" value={kpis.open} icon="circle" accent="#2563EB" />
          <Kpi label="Nudged" value={kpis.nudged} icon="bell" accent="#7C3AED" />
          <Kpi label="Conversion rate" value={`${kpis.conversionRate}%`} icon="check-circle" accent="#16A34A" hint={`${kpis.converted} converted`} />
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {STATE_TABS.map((t) => (
            <button
              key={t.key || 'all'}
              onClick={() => setTab(t.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: `1px solid ${tab === t.key ? 'var(--accent)' : 'var(--border)'}`,
                background: tab === t.key ? 'var(--accent-light)' : 'var(--surface)', color: tab === t.key ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12.5, fontWeight: tab === t.key ? 600 : 400, cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Org</th>
                  <th style={th}>Trigger</th>
                  <th style={th}>State</th>
                  <th style={th}>Value (placeholder)</th>
                  <th style={th}>Opened</th>
                  <th style={th}>Nudged</th>
                  <th style={th}>Converted SKU</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No opportunities in this state.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td}>
                      {r.license?.id
                        ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>{orgNameFor(r)}</Link>
                        : orgNameFor(r)}
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{nudgeTypeLabel(r.trigger_type)}</td>
                    <td style={td}><StatusBadge status={r.state} /></td>
                    <td style={td}>
                      {r.value_estimate != null ? `$${Number(r.value_estimate).toLocaleString()}` : <span style={{ color: 'var(--text-muted)' }}>TODO — rate card</span>}
                    </td>
                    <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{formatRelativeTime(r.opened_at)}</td>
                    <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{r.nudged_at ? formatRelativeTime(r.nudged_at) : '—'}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{r.converted_sku || '—'}</td>
                    <td style={td}>
                      {(r.state === 'open' || r.state === 'nudged') && (
                        <button
                          onClick={() => setDismissTarget(r)}
                          title="Dismiss"
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}
                        >
                          <X size={12} /> Dismiss
                        </button>
                      )}
                      {rowError[r.id] && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>{rowError[r.id]}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>

      <ConfirmDialog
        open={dismissTarget !== null}
        title="Dismiss opportunity?"
        body={dismissTarget ? `Dismiss the "${nudgeTypeLabel(dismissTarget.trigger_type)}" opportunity for ${orgNameFor(dismissTarget)}? It will no longer nudge until re-triggered.` : ''}
        confirmLabel="Dismiss"
        onConfirm={() => { const t = dismissTarget; setDismissTarget(null); if (t) doDismiss(t); }}
        onCancel={() => setDismissTarget(null)}
      />
    </div>
  );
}

export default OpportunitiesPage;
